import React, { useState, useEffect, useCallback } from "react";
import { FormattedMessage } from "react-intl";
import toast from "react-hot-toast";
import Stack from '@mui/material/Stack';
import Fade from '@mui/material/Fade';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';

import useUserDetailState from "./../../hooks/useUserDetailState";
import postStripeCheckout from "../../services/stripe/postStripeCheckout";
import { getSubscriptionType } from "../../services/getSubscriptionType";

import { style } from "./styles";

const CheckIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#00a271" strokeWidth="3">
        <polyline points="20,6 9,17 4,12" />
    </svg>
);

const PlanCard = ({ plan, isCurrentPlan, onSubscribe, loading }) => {
    const isBestOption = plan.name.toLowerCase().includes('business');
    
    return (
        <Card sx={{
            borderRadius: '12px',
            padding: '24px',
            position: 'relative',
            border: isBestOption ? '2px solid #702180' : '1px solid #e0e0e0',
            backgroundColor: '#fff',
            minHeight: '400px',
            display: 'flex',
            flexDirection: 'column',
        }}>
            {isBestOption && (
                <Box sx={{
                    position: 'absolute',
                    top: '-12px',
                    right: '16px',
                    backgroundColor: '#702180',
                    color: 'white',
                    padding: '4px 16px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                }}>
                    BEST OPTION
                </Box>
            )}
            
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 0 }}>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <h3 style={{ margin: 0, fontSize: '20px', color: '#333' }}>{plan.name}</h3>
                </Box>
                
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <span style={{ fontSize: '36px', fontWeight: 'bold', color: '#333' }}>
                        € {plan.original_price}
                    </span>
                    <span style={{ fontSize: '16px', color: '#666' }}> / mese</span>
                </Box>
                
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ flex: 1, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                        <CheckIcon />
                        <span style={{ fontSize: '14px', color: '#555' }}>
                            {(plan.tokens * 4).toLocaleString()} characters for AI tools
                        </span>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                        <CheckIcon />
                        <span style={{ fontSize: '14px', color: '#555' }}>
                            {plan.images} AI-generated images
                        </span>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                        <CheckIcon />
                        <span style={{ fontSize: '14px', color: '#555' }}>
                            {plan.integrations} social media integrations
                        </span>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                        <CheckIcon />
                        <span style={{ fontSize: '14px', color: '#555' }}>
                            Unlimited Grammar Check & Translator
                        </span>
                    </Box>
                </Box>
                
                <Button
                    variant="contained"
                    fullWidth
                    onClick={() => onSubscribe(plan)}
                    disabled={loading || isCurrentPlan}
                    data-testid={`button-subscribe-${plan.id}`}
                    sx={{
                        backgroundColor: isCurrentPlan ? '#ccc' : '#00a271',
                        color: 'white',
                        borderRadius: '12px',
                        padding: '12px 24px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        textTransform: 'none',
                        '&:hover': {
                            backgroundColor: isCurrentPlan ? '#ccc' : '#008a5f',
                        },
                    }}
                >
                    {loading ? (
                        <CircularProgress size={24} color="inherit" />
                    ) : isCurrentPlan ? (
                        <FormattedMessage id="Current Plan" defaultMessage="Current Plan" />
                    ) : (
                        <FormattedMessage id="Subscribe" defaultMessage="Subscribe" />
                    )}
                </Button>
            </CardContent>
        </Card>
    );
};

const Pricing = () => {
    const state = useUserDetailState();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [subscribingPlanId, setSubscribingPlanId] = useState(null);

    const fetchPlans = useCallback(async () => {
        try {
            const result = await getSubscriptionType('euro');
            if (result.type === 'success' && result.data) {
                // API returns {data: [...], user_subscription: "..."}
                const plansArray = Array.isArray(result.data) ? result.data : result.data.data;
                if (plansArray && Array.isArray(plansArray)) {
                    const paidPlans = plansArray.filter(p => p.original_price > 0);
                    setPlans(paidPlans);
                } else {
                    throw new Error('Invalid plans data format');
                }
            } else {
                throw new Error(result.data?.detail || 'Failed to fetch plans');
            }
        } catch (error) {
            console.error('Error fetching plans:', error);
            toast.error('Failed to load subscription plans');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPlans();
    }, [fetchPlans]);

    const handleSubscribe = async (plan) => {
        if (!plan.id) {
            toast.error('Invalid plan');
            return;
        }

        setSubscribingPlanId(plan.id);
        try {
            const response = await postStripeCheckout(plan.id);
            if (response.checkout_url) {
                window.location.href = response.checkout_url;
            } else {
                toast.error('Failed to start checkout');
            }
        } catch (error) {
            console.error('Checkout error:', error);
            toast.error(error.message || 'Payment error. Please try again.');
        } finally {
            setSubscribingPlanId(null);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Fade in={true} timeout={500}>
            <Stack sx={style.stack}>
                <Box sx={style.centerArea}>
                    <Box sx={style.header}>
                        <h1 style={{ 
                            fontSize: '28px', 
                            fontWeight: 'bold', 
                            color: '#333',
                            textAlign: 'center',
                            marginBottom: '8px'
                        }}>
                            <FormattedMessage 
                                id="Choose your plan" 
                                defaultMessage="Choose the best plan for you!" 
                            />
                        </h1>
                        <p style={{ 
                            fontSize: '16px', 
                            color: '#666',
                            textAlign: 'center',
                            marginBottom: '32px'
                        }}>
                            <FormattedMessage 
                                id="Unlock AI features" 
                                defaultMessage="Unlock powerful AI features for your content creation" 
                            />
                        </p>
                    </Box>
                    
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: '24px',
                        maxWidth: '1000px',
                        margin: '0 auto',
                        padding: '0 16px',
                    }}>
                        {plans.map((plan) => (
                            <PlanCard
                                key={plan.id}
                                plan={plan}
                                isCurrentPlan={state.subscription?.value === plan.name}
                                onSubscribe={handleSubscribe}
                                loading={subscribingPlanId === plan.id}
                            />
                        ))}
                    </Box>
                    
                    <Box sx={{ textAlign: 'center', mt: 3, color: '#666', fontSize: '14px' }}>
                        <FormattedMessage 
                            id="Secure payment by Stripe" 
                            defaultMessage="Secure payment powered by Stripe" 
                        />
                    </Box>
                </Box>
            </Stack>
        </Fade>
    );
};

export default Pricing;
